<template src="./Chart.html"></template>

<script>
import { Plotly } from "vue-plotly";

export default {
  name: "Chart",
  props: {
    data: {
      required: true,
      type: Array,
    },
    type: {
      // Scatter, bar, indicator...
      required: false,
      type: String,
    },
    title: {
      required: false,
      type: String,
    },
    layoutOverrides: {
      required: false,
      type: Object,
      default: () => ({}),
    },
  },

  components: {
    Plotly,
  },

  data: () => ({
  }),

  computed: {

    getChartLayout: function () {

      if (this.data == undefined) return [];

      let layout = {
        showlegend: true,
        autosize: true,
        legend: {
          orientation: "h",
          yanchor: "bottom",
          xanchor: "center",
          y: -0.4,
          x: 0.5,
        },
        margin: {
          l: 50,
          r: 50,
          b: 0,
          t: 10,
        },
        hovermode: "x unified",
        xaxis: {
          type: "date",
          //tickformat: "%d-%m",

          showspikes: true,
          spikemode: "across",
          spikesnap: "cursor",
          spikecolor: "#9ca3af",
          spikethickness: 1,
        },
        hoverlabel: {
          bgcolor: "#111827",
          bordercolor: "#374151",
          font: {
            size: 13,
            color: "#f9fafb",
          },
        },
        // xaxis: {
        //   tickangle: 40,
        //   showticklabels: true,
        //   type: "date",
        //   xaxis: {
        //       tickformat: '%d-%m',
        //     },
        //   //categoryorder: "category ascending",
        //   //title: "Date",
        // }, // annual temperature avg
        yaxis: {
          title: "",
        }
      };

      // Scatter charts lay out one y axis per `yaxis` their traces name. Traces
      // without one keep Plotly's default axes - and still get the forecast marker
      // below (an early return here used to skip it).
      if (this.type == 'scatter' && this.data[0] && this.data[0].yaxis) {

        let processedAxis = [];
        const tickCount = 5;

        // Detects the first yaxis present as the base
        const uniqueYAxes = [...new Set(this.data.map(d => d.yaxis))];
        const baseAxis = uniqueYAxes[0];

        const parsedUniqueYAxes = uniqueYAxes.map((item) => {
          if (item == "y") return 1;
          return parseInt(item.replace("y", ""));
        });

        const leftYAxes = parsedUniqueYAxes.filter((item) => item % 2 === 1);
        const rightYAxes = parsedUniqueYAxes.filter((item) => item % 2 === 0);

        const minXDomain = leftYAxes.length > 1 ? 0 + leftYAxes.length * 0.02 : 0;
        const maxXDomain = rightYAxes.length > 1 ? 1 - rightYAxes.length * 0.035 : 1;

        const baseAxisIndex = baseAxis.replace("y", "") || "1";
        const baseAxisKey = baseAxisIndex === "1" ? "y" : "y" + baseAxisIndex;

        // Force the horizontal axis line (x-axis)
        layout.xaxis = {
          anchor: baseAxisKey,
          showline: true,
          linecolor: "black",
          linewidth: 1,
          domain: [minXDomain, maxXDomain]
        };

        for (let i = 0; i < this.data.length; i++) {
          const currentYaxis = this.data[i].yaxis;

          if (!processedAxis.includes(currentYaxis)) {
            const indexAxis = currentYaxis.replace("y", "") || "1";
            const yaxisKey = indexAxis === "1" ? "yaxis" : "yaxis" + indexAxis;

            let _range = this.data[i].range ? [...this.data[i].range] : undefined;

            // START - Calculate the range for each yaxis
            const allData = this.data.filter((item) => item.yaxis === currentYaxis);
            // Gaps (null) must not count as 0, and spreading multi-year series into
            // Math.min/max can overflow the call stack.
            const allY = allData
              .flatMap((item) => item.y)
              .filter((v) => v !== null && v !== undefined && v !== "")
              .map(Number)
              .filter(Number.isFinite);
            const dataMinY = allY.length ? allY.reduce((a, b) => (a < b ? a : b)) : 0;
            const dataMaxY = allY.length ? allY.reduce((a, b) => (a > b ? a : b)) : 0;
            let minY = dataMinY;
            let maxY = dataMaxY;

            const buffer = (maxY - minY) * 0.1 || 1;
            minY = Math.max(0, Math.floor((minY - buffer) / 5) * 5); // Ensure minY is not negative
            maxY = Math.ceil((maxY + buffer) / 5) * 5;

            let _autoRange = [minY, maxY];
            // END - Calculate the range for each yaxis

            // If the range is not defined or is smaller than the auto-calculated range, update it
            if (!_range) {
              _range = _autoRange;
            } else {
              if (dataMinY < _range[0]) {
                _range[0] = _autoRange[0];
              }
              if (dataMaxY > _range[1]) {
                _range[1] = _autoRange[1];
              }
            }

            const step = (_range[1] - _range[0]) / tickCount;
            const tickvals = Array.from({ length: tickCount + 1 }, (_, k) => _range[0] + k * step);

            layout[yaxisKey] = {
              range: _range,
              tickvals: tickvals,
              ticksuffix: this.data[i].ySuffix ? " " + this.data[i].ySuffix : "",
              side: (indexAxis % 2 === 0) ? "right" : "left",
              zeroline: false,
              overlaying: (yaxisKey.replace('axis', '') === baseAxisKey) ? undefined : baseAxisKey,
              showgrid: true,
              ticklabelposition: "outside",
              // Grow the margin to fit the tick labels (e.g. "40 events"), which the
              // fixed 50px left margin would otherwise clip.
              automargin: true,
            };

            // Special adjustment for y2 and y4
            if (currentYaxis === "y4" && this.data.find(d => d.yaxis === "y2")) {
              layout[yaxisKey].position = 1;
            }

            // Special adjustment for y1 and y3
            if ((currentYaxis === "y1" || currentYaxis === "y") && this.data.find(d => d.yaxis === "y3")) {
              layout[yaxisKey].position = 0;
            }

            processedAxis.push(currentYaxis);
          }
        }
      }

      if (this.type == 'indicator') {
        layout.margin = {
          l: 0,
          r: 0,
          b: 20,
          t: 0,
        };
      }

      if (this.type == 'stack') {
        layout['barmode'] = 'stack'
        layout['bargap'] = 0.25
        layout['bargroupgap'] = 0.05
      }

      // Mark where observed data ends and forecast begins
      const forecast = this.getForecastMarker();
      if (forecast) {
        layout.shapes = (layout.shapes || []).concat(forecast.shapes);
        layout.annotations = (layout.annotations || []).concat(forecast.annotation);
      }

      // if (this.layoutOverrides && Object.keys(this.layoutOverrides).length > 0) {
      //   Object.keys(this.layoutOverrides).forEach(key => {
      //     if (layout[key] && typeof layout[key] === 'object' && !Array.isArray(layout[key])) {
      //       layout[key] = { ...layout[key], ...this.layoutOverrides[key] };
      //     } else {
      //       layout[key] = this.layoutOverrides[key];
      //     }
      //   });
      // }

      return layout;
    },

    getChartData: function () {
      // return this.data;
      if (!this.data) return [];

      return this.data.map((t) => {
        if (t && t.hovertemplate) return t;

        const suffix = t && t.ySuffix ? ` ${t.ySuffix}` : "";

        return {
          ...t,
          hoverinfo: "x+y+name",
          hovertemplate:
            "<b>%{fullData.name}</b>: %{y:.2f}" +
            suffix +
            "<extra></extra>",
        };
      });
    },
  },

  methods: {

    /**
     * When the x axis reaches past today, everything from that point on is
     * forecast data. Every chart plots real dates (compared seasons are moved
     * onto the selected period's own dates), so today is compared as a full
     * date: a month/day match would flag observed data on multi-year axes.
     * Returns the shaded forecast band, the dashed vertical line placed on
     * today and its "Forecast" label, or null when the axis stays in the past.
     */
    getForecastMarker() {
      if (this.type == 'indicator' || !this.data || this.data.length === 0) return null;

      const DATE_RE = /^\d{4}-\d{2}-\d{2}/;

      // Day-level x values of every trace, as "YYYY-MM-DD"
      let dates = [];
      this.data.forEach(trace => {
        if (!trace || !Array.isArray(trace.x)) return;
        trace.x.forEach(value => {
          if (typeof value === 'string' && DATE_RE.test(value)) dates.push(value.slice(0, 10));
        });
      });
      if (dates.length === 0) return null;

      const pad = n => String(n).padStart(2, '0');

      const now = new Date();
      const today = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;

      const min = dates.reduce((a, b) => (a < b ? a : b));
      const max = dates.reduce((a, b) => (a > b ? a : b));

      // Nothing to flag while the axis stays at or behind today
      if (max <= today) return null;
      const x = today < min ? min : today;

      const COLOR = '#9ca3af';
      const BAND_COLOR = 'rgba(253, 224, 71, 0.18)';

      return {
        shapes: [
          // Shaded band covering everything from today to the end of the axis
          {
            type: 'rect',
            xref: 'x',
            yref: 'paper',
            x0: x,
            x1: max,
            y0: 0,
            y1: 1,
            fillcolor: BAND_COLOR,
            line: { width: 0 },
            layer: 'below',
          },
          {
            type: 'line',
            xref: 'x',
            yref: 'paper',
            x0: x,
            x1: x,
            y0: 0,
            y1: 1,
            line: { color: COLOR, width: 2, dash: 'dash' },
          },
        ],
        annotation: {
          x,
          xref: 'x',
          // Sits just inside the plot area: anchored above it the top margin
          // clips the label.
          y: 0.98,
          yref: 'paper',
          text: 'Forecast',
          showarrow: false,
          xanchor: 'left',
          yanchor: 'top',
          xshift: 4,
          font: { size: 22, color: COLOR },
        },
      };
    },
  }
};
</script>

<style lang="scss" src="./Chart.scss"></style>